import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Calendar as CalendarIcon, LogOut, RefreshCcw, Trash2, Mail } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "seated", "cancelled"];

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  seated: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-stone-200 text-stone-600 border-stone-300",
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [date, setDate] = useState(new Date());
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // reservation object pending delete

  const dateStr = useMemo(() => (date ? format(date, "yyyy-MM-dd") : ""), [date]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get("/admin/reservations", { params: { date: dateStr, status: tab } }),
        api.get("/admin/stats"),
      ]);
      setRows(list);
      setStats(st);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [dateStr, tab, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reservations/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const deleteReservation = async (id) => {
    try {
      await api.delete(`/admin/reservations/${id}`);
      toast.success("Reservation deleted");
      setSelected((s) => (s && s.id === id ? null : s));
      setConfirmDelete(null);
      load();
    } catch {
      toast.error("Could not delete reservation");
    }
  };

  const reassignRegular = async (id) => {
    try {
      const { data } = await api.patch(`/admin/reservations/${id}/reassign-regular`);
      toast.success("Confirmed with a regular table (balcony cleared)");
      setSelected((s) => (s && s.id === id ? { ...s, ...data } : s));
      load();
    } catch {
      toast.error("Could not reassign reservation");
    }
  };

  const resendEmail = async (r) => {
    try {
      const { data } = await api.post(`/admin/reservations/${r.id}/resend-email`);
      toast.success(`Confirmation email resent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend email");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="border-b border-[#E5E0D8] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-display text-xl text-[#1A2B3C]">Caldera View Bar Restaurant</Link>
            <span className="overline">Reservations Console</span>
            <Link to="/admin/content" className="text-sm text-[#8C7A6B] hover:text-[#D35400] hidden sm:inline" data-testid="admin-nav-content">
              Site content →
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#8C7A6B] hidden sm:inline" data-testid="admin-user-label">
              {user?.email}
            </span>
            <Button variant="outline" onClick={handleLogout} className="rounded-full border-[#E5E0D8] h-9" data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-8">
        {/* Stats */}
        <section className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
          <Stat label="Total" value={stats?.total ?? "—"} testid="stat-total" />
          <Stat label="Today" value={stats?.today ?? "—"} testid="stat-today" />
          <Stat label="Pending" value={stats?.pending ?? "—"} testid="stat-pending" />
          <Stat label="Confirmed" value={stats?.confirmed ?? "—"} testid="stat-confirmed" />
          <Stat label="Seats Today" value={stats?.seats_today ?? "—"} testid="stat-seats" />
        </section>

        {/* Filters */}
        <section className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-10 border-[#E5E0D8] bg-white" data-testid="admin-date-trigger">
                  <CalendarIcon className="h-4 w-4 mr-2 text-[#D35400]" />
                  {date ? format(date, "d LLL yyyy") : "All dates"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={date} onSelect={setDate} />
              </PopoverContent>
            </Popover>
            <Button
              variant="ghost"
              onClick={() => setDate(undefined)}
              className="h-10 text-[#8C7A6B]"
              data-testid="admin-clear-date"
            >
              Clear
            </Button>
            <Button variant="ghost" onClick={load} className="h-10 text-[#1A2B3C]" data-testid="admin-refresh">
              <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>

          <Tabs value={tab} onValueChange={setTab} data-testid="admin-status-tabs">
            <TabsList className="bg-white border border-[#E5E0D8]">
              <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
              {STATUSES.map((s) => (
                <TabsTrigger key={s} value={s} data-testid={`tab-${s}`} className="capitalize">{s}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </section>

        {/* Table */}
        <section className="bg-white border border-[#E5E0D8] rounded-lg overflow-hidden">
          <Table data-testid="admin-reservations-table">
            <TableHeader>
              <TableRow className="bg-[#F9F6F0]">
                <TableHead>Reference</TableHead>
                <TableHead>Guest</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Time</TableHead>
                <TableHead>Party</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#8C7A6B]">Loading…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-[#8C7A6B]" data-testid="admin-empty">No reservations for this filter.</TableCell></TableRow>
              )}
              {!loading && rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-[#F9F6F0]/60 cursor-pointer" onClick={() => setSelected(r)} data-testid={`row-${r.reference_code}`}>
                  <TableCell className="font-display text-[#D35400]">
                    <div className="flex items-center gap-2">
                      {r.reference_code}
                      {r.balcony_requested && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-[#D35400]/10 text-[#D35400] border border-[#D35400]/30 text-[10px] uppercase tracking-widest px-2 py-0.5"
                          data-testid={`row-balcony-${r.reference_code}`}
                          title="Balcony table requested"
                        >
                          Balcony
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-[#1A2B3C]">{r.name}</div>
                    <div className="text-xs text-[#8C7A6B]">{r.email}</div>
                  </TableCell>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.time_slot}</TableCell>
                  <TableCell>{r.party_size}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`capitalize ${STATUS_STYLES[r.status]}`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`row-actions-${r.reference_code}`}>Set status</Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {STATUSES.map((s) => (
                          <DropdownMenuItem key={s} onClick={() => updateStatus(r.id, s)} className="capitalize" data-testid={`row-action-${r.reference_code}-${s}`}>
                            {s}
                          </DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setConfirmDelete(r)}
                          className="text-[#C0392B] focus:text-[#C0392B]"
                          data-testid={`row-action-${r.reference_code}-delete`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      </main>

      {/* Details Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white" data-testid="admin-details-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Reservation {selected?.reference_code}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <Detail label="Guest" value={selected.name} />
              <Detail label="Email" value={selected.email} />
              <Detail label="Phone" value={selected.phone} />
              <Detail label="Date · Time" value={`${selected.date} · ${selected.time_slot}`} />
              <Detail label="Party size" value={String(selected.party_size)} />
              <Detail
                label="Balcony"
                value={
                  selected.balcony_requested ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#D35400]/10 text-[#D35400] border border-[#D35400]/30 text-xs px-2 py-0.5" data-testid="dialog-balcony-yes">
                      Requested · minimum‑spend applies
                    </span>
                  ) : (
                    <span className="text-[#8C7A6B]">Not requested</span>
                  )
                }
              />
              {selected.balcony_requested && (
                <div className="rounded-lg border border-[#D35400]/30 bg-[#D35400]/5 p-4 text-sm text-[#1A2B3C]" data-testid="dialog-balcony-panel">
                  <div className="font-medium mb-1">Balcony not available?</div>
                  <p className="text-xs text-[#8C7A6B] mb-3">
                    Assign this guest a regular table instead of cancelling. This clears the balcony request and confirms the reservation.
                  </p>
                  <Button
                    onClick={() => reassignRegular(selected.id)}
                    className="btn-terracotta h-9 rounded-full px-5 text-sm"
                    data-testid="dialog-action-reassign-regular"
                  >
                    Confirm with regular table
                  </Button>
                </div>
              )}
              <Detail label="Status" value={<Badge variant="outline" className={`capitalize ${STATUS_STYLES[selected.status]}`}>{selected.status}</Badge>} />
              <div>
                <div className="overline">Special requests</div>
                <div className="mt-1 text-[#1A2B3C]">{selected.special_requests || "—"}</div>
              </div>
              <div className="pt-4 border-t border-[#E5E0D8] flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <Button
                    key={s}
                    variant={selected.status === s ? "default" : "outline"}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`h-9 rounded-full capitalize ${selected.status === s ? "btn-terracotta" : "border-[#E5E0D8]"}`}
                    data-testid={`dialog-action-${s}`}
                  >
                    Mark {s}
                  </Button>
                ))}
                <Button
                  variant="outline"
                  onClick={() => resendEmail(selected)}
                  className="h-9 rounded-full border-[#E5E0D8] text-[#1A2B3C]"
                  data-testid="dialog-action-resend-email"
                >
                  <Mail className="h-4 w-4 mr-2" /> Resend email
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(selected)}
                  className="h-9 rounded-full border-[#C0392B]/30 text-[#C0392B] hover:bg-[#C0392B]/5 hover:text-[#C0392B] ml-auto"
                  data-testid="dialog-action-delete"
                >
                  <Trash2 className="h-4 w-4 mr-2" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Delete reservation permanently?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8C7A6B] leading-relaxed">
              This will permanently remove reservation{" "}
              <span className="font-medium text-[#1A2B3C]">{confirmDelete?.reference_code}</span>
              {" "}for {confirmDelete?.name}. This action cannot be undone.
              {" "}Consider marking it as <em>cancelled</em> instead if you want to keep it for your records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteReservation(confirmDelete.id)}
              className="bg-[#C0392B] text-white hover:bg-[#a83224]"
              data-testid="delete-confirm"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-[#E5E0D8] rounded-lg p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="mt-2 font-display text-3xl text-[#1A2B3C]">{value}</div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="grid grid-cols-3 items-baseline gap-4">
      <div className="overline">{label}</div>
      <div className="col-span-2 text-[#1A2B3C]">{value}</div>
    </div>
  );
}
